import { Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import SEO from '@/components/SEO';
import { 
  ArrowRight, 
  Check,
  ShoppingCart, 
  Smartphone, 
  Building2, 
  Stethoscope, 
  GraduationCap, 
  Wallet,
  Users,
  Palette,
  Rocket,
  Target,
  TrendingUp
} from 'lucide-react';
import '@/styles/forms.css';
import '@/styles/landing.css';

/**
 * /use-cases/ — converted to the current brand system on 2026-08-19.
 *
 * PRESENTATION ONLY. Every string on this page is byte-identical to the
 * version it replaces: the h1, the four h2s, the thirteen h3s, the four
 * section labels, all six industry blocks, all four persona blocks, the three
 * steps, the one surviving stat and both CTA pairs, including the link anchor
 * text. The SEO block — title, description, canonical and the WebPage +
 * BreadcrumbList JSON-LD — is unchanged. The deletions list is empty.
 *
 * ── WHAT CHANGED, AND WHY ───────────────────────────────────────────────────
 *
 * 1. THE THREE OFF-PALETTE HUES ARE GONE. The industry cards colour-coded
 *    themselves with #3B82F6 (blue), #8B5CF6 (violet) and #EC4899 (pink),
 *    plus --success and --severity-major-text borrowed as decoration. The
 *    system has no violet and no pink, --success is scoped in tokens.css to
 *    "form validation only. NEVER a score", and --severity-major-text means
 *    one thing. Six industries do not need six hues to be distinct — they
 *    have six names and six icons. All six now take the accent tint frame the
 *    persona cards already used, so the page reads as one palette.
 *
 * 2. TYPE COMES FROM THE ROLES. .uxa-h1 / .uxa-h2 / .uxa-lead / --type-dense-size
 *    replace the inline 18px / 17px / clamp() objects.
 *
 * 3. THE SHELLS COME FROM landing.css. .uxa-landing-section, .uxa-feature-card,
 *    .uxa-grid-3 / .uxa-grid-2, .uxa-dark-panel and .uxa-closing are the
 *    homepage's surfaces, reused rather than re-cut. No new CSS ships with
 *    this change.
 *
 * 4. THE TONAL BREAK MOVED. The page had two dark bands (How It Works was
 *    sand, the CTA was --ink-strong). It now has one: How It Works is the
 *    dark panel, and the closing CTA is the light .uxa-closing band with the
 *    ruled grid and bloom, same as the homepage. Because the CTA ground went
 *    from ink to sand, the italic "UX" moved from --accent-soft (chosen for a
 *    dark ground) back to --accent-text, which is the 6.02:1 value.
 *
 * ── NOT CHANGED, DELIBERATELY ───────────────────────────────────────────────
 *
 * The flagged claims stay flagged, not fixed. "4 min Avg. audit time" is
 * unverified but not demonstrably false, and the two removal blocks below are
 * carried forward verbatim so the record of what left this page survives a
 * restyle. Nothing here is a content edit.
 */

const industries = [
  {
    id: 'ecommerce',
    icon: ShoppingCart,
    name: 'E-commerce',
    headline: 'Reduce cart abandonment & boost conversions',
    description: 'Identify checkout friction, optimize product pages, and improve mobile shopping experiences that directly impact revenue.',
    painPoints: ['High cart abandonment rates', 'Low mobile conversion', 'Confusing checkout flows', 'Poor product discovery'],
  },
  {
    id: 'saas',
    icon: Building2,
    name: 'B2B SaaS',
    // "Improve onboarding & reduce churn" / "Surface onboarding gaps" /
    // "Complex onboarding flows" — instance 9. Onboarding stopped being an
    // assessable dimension on 2026-07-15 because a flow cannot be audited from
    // a single page. Rewritten to what a capture can actually show, matching
    // the wording already used on /use-cases/saas/.
    // "& churn" removed under the false-claim exception, ruled by the owner
    // 2026-08-19. Churn is retention measured over time across a population;
    // a single-page audit cannot see it. Exactly the shape of the onboarding
    // claim removed on 2026-07-15 — a flow cannot be audited from one capture,
    // and neither can a cohort.
    //
    // "Reduce signup friction" is the half that survives, and it is the half a
    // capture can actually evidence.
    headline: 'Reduce signup friction',
    description: 'Surface friction on your signup and trial screens, simplify complex dashboards, and find what makes trial users give up.',
    painPoints: ['Low trial-to-paid conversion', 'Overloaded signup screens', 'Feature discovery issues', 'Dashboard overwhelm'],
  },
  {
    id: 'fintech',
    icon: Wallet,
    name: 'FinTech',
    headline: 'Build trust & streamline transactions',
    description: 'Optimize payment flows, enhance security perception, and create frictionless financial experiences that users trust.',
    painPoints: ['Transaction abandonment', 'Trust & security concerns', 'Complex verification flows', 'Poor error handling'],
  },
  {
    id: 'healthcare',
    icon: Stethoscope,
    name: 'HealthTech',
    headline: 'Simplify patient experiences',
    description: 'Make healthcare apps accessible, compliant, and easy to use for patients of all ages and technical abilities.',
    painPoints: ['Accessibility compliance', 'Complex booking flows', 'Information overload', 'Multi-generational users'],
  },
  {
    id: 'edtech',
    icon: GraduationCap,
    name: 'EdTech',
    headline: 'Engage learners & improve completion',
    description: 'Optimize learning paths, reduce course dropout rates, and create engaging educational experiences.',
    painPoints: ['Low course completion', 'Engagement drop-off', 'Complex navigation', 'Progress tracking issues'],
  },
  {
    id: 'mobile',
    icon: Smartphone,
    name: 'Mobile Apps',
    headline: 'Optimize for thumb-friendly interactions',
    description: 'Ensure touch targets, gestures, and mobile-specific patterns meet user expectations and platform guidelines.',
    painPoints: ['Small touch targets', 'Gesture confusion', 'Performance issues', 'Platform inconsistency'],
  },
];

/* Inline prose link. --accent-text is the text-safe orange (#B3320A); the
   fill orange fails AA at this size, which is the whole reason the two exist.
   Underlined, because colour alone is not a link affordance. Matches how
   HomeLanding styles its prose links. */
const LINK = { color: 'var(--accent-text)', textDecoration: 'underline' };

const personas = [
  {
    role: 'Product Managers',
    icon: Target,
    description: 'Get data-driven insights to prioritize your roadmap and justify UX investments to stakeholders.',
    benefits: ['Prioritized fix recommendations', 'Effort vs. impact scoring', 'Stakeholder-ready reports']
  },
  {
    role: 'UX Designers',
    icon: Palette,
    description: 'Validate designs, catch issues early, and get specific recommendations for improvement.',
    benefits: ['Annotated screenshots', 'Heuristic analysis', 'Accessibility compliance checks']
  },
  {
    role: 'Startup Founders',
    icon: Rocket,
    description: 'Ship better products faster without hiring expensive UX consultants.',
    benefits: ['Affordable audits', 'Quick turnaround', 'Actionable quick wins']
  },
  {
    role: 'Agency Teams',
    icon: Users,
    description: 'Deliver professional UX audits to clients with white-label branding.',
    benefits: ['White-label PDF reports', 'Client-ready deliverables', 'Scalable process']
  },
];

const steps = [
  { step: '01', icon: Smartphone, title: 'Upload', desc: 'Share screenshots, paste a URL, or upload a PDF of your app screens.' },
  // WAS: "AI examines every pixel across 6 UX dimensions in under 4 minutes."
  // Removed under the false-claim exception, ruled by the owner 2026-08-19.
  //
  //   "every pixel" misdescribes the pipeline in the direction that flatters
  //   it least accurately. A URL audit collects desktop and mobile screenshots,
  //   the served HTML, and DOM-COMPUTED measurements — and the analyser is told
  //   at length (claude_analyzer.py:107) that it may NOT judge text size from
  //   pixels, only from the measured DOM values. The strongest evidence this
  //   product has is the thing "every pixel" writes out of the sentence.
  //
  //   "in under 4 minutes" is the same unsourced timing as the stat band, and
  //   it went for the same reason. See docs/DECISIONS.md.
  { step: '02', icon: Target, title: 'Analyze', desc: 'AI reviews every screen you give it and scores 6 UX dimensions.' },
  { step: '03', icon: TrendingUp, title: 'Improve', desc: 'Get scored findings, prioritized fixes, and a downloadable PDF report.' },
];

/* The `stats` array went with the band above. Its contents are preserved in
 * that comment and in docs/DECISIONS.md rather than here, because a data array
 * with no consumer is the shape that gets quietly re-rendered by the next
 * person who finds it. */


export default function UseCasesPage() {
  const { login, user } = useAuth();

  return (
    <div>
      <SEO
        title="UX Audit Use Cases: SaaS, E-commerce & FinTech"
        description="UXAuditPro helps E-commerce, B2B SaaS, FinTech, HealthTech & mobile teams fix UX problems fast. See the friction that matters most in your industry. Try free."
        canonical="/use-cases"
        structuredData={{
          '@context': 'https://schema.org',
          '@type': 'WebPage',
          name: 'UX Audit Use Cases',
          url: 'https://www.uxauditpro.com/use-cases/',
          description: 'AI-powered UX audits for E-commerce, B2B SaaS, FinTech, HealthTech, EdTech, and Mobile Apps.',
          breadcrumb: {
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://www.uxauditpro.com/' },
              { '@type': 'ListItem', position: 2, name: 'Use Cases', item: 'https://www.uxauditpro.com/use-cases/' },
            ],
          },
        }}
      />

      {/* ═══ HERO ══════════════════════════════════════════════════════════════
          Centred, over the 72px grid wash the homepage hero uses. The eyebrow,
          h1, lead and both actions are unchanged strings. */}
      <header className="uxa-pt-xl px-6 hero" style={{ position: 'relative', textAlign: 'center' }}>
        <div className="uxa-hero-grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 1180, margin: '0 auto' }}>
          <span className="section-label" style={{ display: 'inline-block', marginBottom: 22 }}>
            Use Cases
          </span>
          <h1 className="uxa-h1" style={{ color: 'var(--ink-1)', maxWidth: 900, margin: '0 auto' }}>
            UX audits for{' '}<br /><span style={{ color: 'var(--accent-display)', fontStyle: 'italic', fontWeight: 700 }}>every team</span>.
          </h1>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', maxWidth: 620, margin: '20px auto 0' }}>
            Whether you're optimizing e-commerce checkout or simplifying a SaaS dashboard, UXAuditPro helps you find and fix UX issues fast.
          </p>
          <div style={{ display: 'flex', gap: 22, justifyContent: 'center', alignItems: 'center', marginTop: 34, flexWrap: 'wrap' }}>
            {user ? (
              <Link to="/audit/new" className="uxa-submit uxa-submit--auto">
                Start Free Audit
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </Link>
            ) : (
              <button onClick={login} className="uxa-submit uxa-submit--auto">
                Start Free Audit
                <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
              </button>
            )}
            <Link to="/case-studies/" className="uxa-submit uxa-submit--auto uxa-submit--secondary">
              View Case Studies
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </header>

      {/* ═══ INDUSTRIES ════════════════════════════════════════════════════════
          Centred heading, cards left. One palette across all six: the icon sits
          in the accent tint frame, and the card is told apart by its name and
          its icon rather than by a hue the system does not own. */}
      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl">
        <span className="section-label">Industries</span>
        <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0', maxWidth: 720 }}>
          Built for your industry
        </h2>
        {/* WAS: "AI-powered analysis trained on thousands of apps across every
            vertical". Removed under the false-claim exception, ruled by the
            owner 2026-08-19. Three separate untruths in eleven words:

            "trained on thousands of apps" — we have 106 audits, and they are
            not training data at all. Nothing in this pipeline fine-tunes on
            customer audits; the model is a foundation model handed a capture.
            Same class as the "n=142" benchmark.

            "every vertical" — an unbounded coverage claim with nothing behind
            it.

            "across every vertical" also implies per-industry specialisation
            that the analyser is explicitly instructed NOT to do:
            claude_analyzer.py line 164 tells it to "Classify from what is
            VISIBLY on the page and in the extracted structure. Never from the
            domain name or from what sites like this usually are."

            The replacement is the true version of the same point, and it is
            what makes the section cohere: the industries differ, the audit
            does not. */}
        <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px auto 0', maxWidth: 620 }}>
          The same six-dimension audit, whatever you are building
        </p>

        <div className="uxa-grid-3" style={{ marginTop: 56 }}>
          {industries.map((industry) => (
            <div key={industry.id} className="uxa-feature-card" style={{ display: 'flex', flexDirection: 'column' }}>
              <div className="uxa-feature-card__bloom" aria-hidden="true" />

              {/* Decorative, exactly as FeaturePreview is on the homepage: a
                  small static artefact made of things the product produces —
                  here the industry's mark over four finding rules, one per pain
                  point below. It carries no text a reader needs, so it is
                  aria-hidden and the h3 and paragraphs carry the meaning. */}
              <div className="uxa-feature-card__frame" aria-hidden="true" style={{ display: 'flex', gap: 14, alignItems: 'center' }}>
                <span
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 'var(--radius-field)',
                    background: 'var(--accent-tint)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 'none',
                  }}
                >
                  <industry.icon className="w-5 h-5" style={{ color: 'var(--accent-text)' }} />
                </span>
                {/* Capped. .uxa-grid-3 collapses to one column at 768, and an
                    uncapped bar stack stretched to ~700px there, which reads as
                    a loading skeleton rather than as a finding list. */}
                <span style={{ flex: 1, maxWidth: 240, display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {[100, 72, 88, 54].map((w, i) => (
                    <span
                      key={i}
                      style={{
                        display: 'block',
                        height: 5,
                        width: `${w}%`,
                        borderRadius: 3,
                        background: i === 0 ? 'var(--accent)' : 'var(--track-empty)',
                      }}
                    />
                  ))}
                </span>
              </div>

              <h3 className="uxa-h3" style={{ color: 'var(--ink-1)', margin: '24px 0 8px' }}>
                {industry.name}
              </h3>
              <p
                /* The industry hue is gone from the headline AND from the tile.
                   As 14px/600 text it measured 3.5-4.2:1 on the built page — an
                   AA failure on every card — and as a tile fill it was one of
                   three values the system does not own. See the header. */
                style={{ fontSize: 'var(--type-dense-size)', fontWeight: 700, color: 'var(--ink-1)', margin: '0 0 12px' }}
              >
                {industry.headline}
              </p>
              <p className="uxa-body" style={{ color: 'var(--ink-2)', margin: 0, flex: 1 }}>
                {industry.description}
              </p>

              {/* Pain Points */}
              <div style={{ marginTop: 24, paddingTop: 20, borderTop: '1px solid var(--border-2)' }}>
                <p className="section-label" style={{ margin: '0 0 12px' }}>
                  Common Pain Points
                </p>
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 9 }}>
                  {industry.painPoints.map((point, i) => (
                    <li
                      key={i}
                      style={{ display: 'flex', alignItems: 'flex-start', gap: 10, fontSize: 'var(--type-dense-size)', color: 'var(--ink-2)' }}
                    >
                      {/* The pain points were 10px pills, four to a card, 24 to
                          a page. 10px is below anything this system sets and
                          the pill wall read as tags rather than as the list it
                          is. Same idiom as the homepage's source list. */}
                      <span
                        aria-hidden="true"
                        style={{ width: 5, height: 5, borderRadius: '50%', background: 'var(--accent)', flex: 'none', marginTop: 8 }}
                      />
                      {point}
                    </li>
                  ))}
                </ul>
              </div>

              {/* The metrics strip was removed on 2026-08-18. Each of the six
                  industry cards carried three outcome figures — 18 in total,
                  all unsourced, and several of them ("+47% conversion",
                  "+156% mobile revenue", "-41% bounce rate") lifted straight
                  from the fabricated case studies deleted in the same batch.
                  Removed rather than reworded: there is no source to soften
                  them towards, so any rewrite invents a fresh claim. */}
            </div>
          ))}
        </div>
      </section>

      {/* ═══ WHO IT'S FOR ══════════════════════════════════════════════════════ */}
      <section className="uxa-landing-section uxa-landing-section--center uxa-pt-xl">
        <span className="section-label">Who It's For</span>
        <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: '20px auto 0', maxWidth: 720 }}>
          Made for product teams
        </h2>
        <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '16px auto 0', maxWidth: 620 }}>
          From solo founders to enterprise design teams
        </p>

        <div className="uxa-grid-2" style={{ marginTop: 56 }}>
          {personas.map((persona) => (
            <div key={persona.role} className="uxa-feature-card">
              {/* NO BLOOM ON THIS CARD. .uxa-feature-card__bloom is positioned
                  top: -70px over 180px, sized to sit behind a frame at the TOP
                  of the card. Here the white frame is at the bottom, so the
                  bloom landed on the h3 and the paragraph instead — still AA on
                  the measured page, but a gradient behind running text is not
                  what the pattern is for. The industry cards above, whose frame
                  is at the top, keep it. */}
              <div style={{ display: 'flex', gap: 20, alignItems: 'flex-start' }}>
                <span
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 'var(--radius-md)',
                    background: 'var(--accent-tint)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flex: 'none',
                  }}
                >
                  <persona.icon className="w-6 h-6" style={{ color: 'var(--accent-text)' }} aria-hidden="true" />
                </span>
                <div>
                  <h3 className="uxa-h3" style={{ color: 'var(--ink-1)', margin: '0 0 8px' }}>
                    {persona.role}
                  </h3>
                  <p className="uxa-body" style={{ color: 'var(--ink-2)', margin: 0 }}>
                    {persona.description}
                  </p>
                </div>
              </div>

              {/* The benefits sit in the white inner frame, which is the shape
                  every card on the homepage makes: sand card, white panel, the
                  thing the card is actually promising inside it. The list was
                  loose text under the paragraph and read as an afterthought. */}
              <ul className="uxa-feature-card__frame" style={{ listStyle: 'none', margin: '22px 0 0', padding: 'var(--space-5)', display: 'flex', flexDirection: 'column', gap: 12 }}>
                {persona.benefits.map((benefit, i) => (
                  <li
                    key={i}
                    style={{ display: 'flex', alignItems: 'flex-start', gap: 12, fontSize: 'var(--type-dense-size)', fontWeight: 500, color: 'var(--ink-1)' }}
                  >
                    <span className="uxa-price-tick" style={{ marginTop: 0, width: 20, height: 20 }} aria-hidden="true">
                      <Check size={12} />
                    </span>
                    {benefit}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* ── THE HUB LINKS ITS OWN CHILDREN, 2026-08-26 ──────────────────────
            Added because it did not. Measured on the built site: this page
            carried 14 links and every one was nav or footer chrome. The three
            pages it is the hub FOR had one inbound link each, from the
            homepage, and /use-cases/saas/ and /use-cases/agencies/ had both
            been sitting in Search Console as "Discovered, currently not
            indexed" with Last crawled: N/A since 27 July.

            A sitemap tells Google a URL exists. Internal links tell it the URL
            matters, and a page reachable by one link from one place reads as a
            page nobody thinks is important. Resubmitting the sitemap cannot
            fix that; this can.

            HONEST LIMIT: /use-cases/startups/ also had one inbound link and IS
            indexed, so thin linking is not a proven cause here. It is the
            strongest lever available, not a diagnosis.

            A row rather than links on the persona cards: the cards are four
            ROLES (Product Managers, UX Designers, Startup Founders, Agency
            Teams) and the children are three AUDIENCES. Two of the four cards
            would map to the same page and one page would map to none, so
            linking the cards would either duplicate or leave a gap. Nothing
            above is reworded; this is one new line under the grid.

            Anchor text matches what the homepage already uses for these three
            links, so the two internal references agree. */}
        <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '40px auto 0', maxWidth: 620 }}>
          Deeper dives by team:{' '}
          <Link to="/use-cases/saas/" style={LINK}>SaaS use cases</Link>,{' '}
          <Link to="/use-cases/agencies/" style={LINK}>Agency use cases</Link>, and{' '}
          <Link to="/use-cases/startups/" style={LINK}>Startup use cases</Link>.
        </p>
      </section>

      {/* ═══ HOW IT WORKS ══════════════════════════════════════════════════════
          The page's one tonal break, on the dark panel with the 72px white
          grid. It was a sand band, and the closing CTA below it was the dark
          one; the page now breaks tone once, where the process is. */}
      <section className="uxa-landing-section uxa-pt-xl">
        <div className="uxa-dark-panel">
          <div style={{ textAlign: 'center' }}>
            <span className="uxa-dark-panel__eyebrow">How It Works</span>
            <h2 className="uxa-h2" style={{ color: 'var(--text-inverse)', margin: '20px auto 0', maxWidth: 720 }}>
              Three steps to better UX
            </h2>
          </div>

          <div className="uxa-grid-3" style={{ marginTop: 56, textAlign: 'center' }}>
            {steps.map((item) => (
              <div key={item.step}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 'var(--type-dense-size)',
                    fontWeight: 700,
                    letterSpacing: '0.12em',
                    color: 'var(--accent-soft)',
                  }}
                >
                  STEP {item.step}
                </span>
                <div
                  style={{
                    width: 52,
                    height: 52,
                    borderRadius: 'var(--radius-md)',
                    background: 'rgba(255, 255, 255, 0.08)',
                    border: '1px solid rgba(255, 255, 255, 0.14)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    margin: '22px auto',
                  }}
                >
                  <item.icon className="w-6 h-6" style={{ color: 'var(--accent-soft)' }} aria-hidden="true" />
                </div>
                <h3 className="uxa-h3" style={{ color: 'var(--text-inverse)', margin: '0 0 8px' }}>
                  {item.title}
                </h3>
                <p className="uxa-body" style={{ color: 'var(--ink-4)', margin: 0 }}>
                  {item.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ STATS — REMOVED 2026-08-19 ═══════════════════════════════════════
          The band is gone, not restyled.

          It held one figure, "4 min · Avg. audit time", in a layout built for
          four. The other three ("500+ Product teams", "2,400+ Audits
          completed", "14,000+ Issues identified") were removed on 2026-08-17
          as demonstrably false — 29x, 23x and ~7x overstatements — and nobody
          re-laid-out what was left, so an unverified number sat alone in a
          frame that read as "three stats failed to load".

          The owner's ruling was: compute it from real ux_audits timestamps and
          cite it, or drop the section. Do not leave one unverified number alone
          in a band built for four.

          IT IS NOT COMPUTABLE FROM HERE, AND THAT IS THE REASON IT WENT. The
          data exists — ux_audits carries created_at and completed_at, written
          in server.py at insert and at delivery — but the collection lives in
          production Mongo, which this environment has no route to (27017 is
          closed here and there is no client). Estimating it would have produced
          exactly the kind of number this rule exists to stop, so the section
          took the owner's second alternative.

          TO BRING IT BACK, honestly: aggregate
          (completed_at - created_at) over ux_audits where status = DELIVERED,
          report the median rather than the mean (a handful of retried audits
          drag a mean badly), state the sample size and the window next to the
          figure, and pin it in a test the way the pricing figures are pinned.
          Then it is a measurement and not a slogan. */}

      {/* ═══ CLOSING CTA ═══════════════════════════════════════════════════════
          The homepage's closing band: sand ground, masked 56px grid, spectrum
          bloom under the primary action only. The signed-in / signed-out fork
          is inlined at both sites, never wrapped in a local component — see
          styles/__tests__/filled-control-needs-a-ground.test.js. */}
      <section className="uxa-closing" style={{ marginTop: 120 }}>
        <div className="uxa-closing__grid" aria-hidden="true" />
        <div style={{ position: 'relative', maxWidth: 720, margin: '0 auto', textAlign: 'center' }}>
          <h2 className="uxa-h2" style={{ color: 'var(--ink-1)', margin: 0 }}>
            {/* --accent-text is 6.02:1 on this sand ground. The previous
                --accent-soft was chosen for the ink band this section used to
                be, where it measured 10.21:1 and --accent-text failed at
                2.71:1. Same rule, ground reversed. */}
            Ready to improve your <span style={{ color: 'var(--accent-display)', fontStyle: 'italic', fontWeight: 700 }}>UX</span>?
          </h2>
          <p className="uxa-lead" style={{ color: 'var(--ink-2)', margin: '18px auto 0', maxWidth: 520 }}>
            Start with a free audit and see what you're missing. No credit card required.
          </p>
          <div style={{ display: 'flex', gap: 22, justifyContent: 'center', alignItems: 'center', marginTop: 40, flexWrap: 'wrap' }}>
            <div style={{ position: 'relative', display: 'inline-block', isolation: 'isolate' }}>
              <span style={{ position: 'relative', zIndex: 1, display: 'inline-flex' }}>
                {user ? (
                  <Link to="/audit/new" className="uxa-submit uxa-submit--auto">
                    Start Free Audit
                    <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                  </Link>
                ) : (
                  <button onClick={login} className="uxa-submit uxa-submit--auto">
                    Start Free Audit
                    <span className="uxa-submit-knob" aria-hidden="true"><ArrowRight className="w-4 h-4" /></span>
                  </button>
                )}
              </span>
            </div>
            <Link to="/ux-auditor/#pricing" className="uxa-submit uxa-submit--auto uxa-submit--secondary">
              View Pricing
              <ArrowRight className="w-4 h-4" aria-hidden="true" />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
